import React, { useState, useEffect } from 'react';
import { Button } from './button';
import { Minus, Plus } from 'lucide-react';

/**
 * NumberStepper - A +/- stepper component for number inputs
 * Supports both button clicks and direct text input
 * Updated for mobile-responsive spec (ontof-quotation-v2-3.html)
 */
const NumberStepper = ({ 
  value = 0, 
  onChange, 
  min = 0, 
  max = 99,
  disabled = false,
  label,
  size = 'default' // 'default' | 'sm'
}) => {
  const [inputValue, setInputValue] = useState(String(value));

  // Sync internal state with external value
  useEffect(() => {
    setInputValue(String(value));
  }, [value]);

  const handleDecrement = () => {
    if (value > min && !disabled) {
      onChange(value - 1);
    }
  };

  const handleIncrement = () => {
    if (value < max && !disabled) {
      onChange(value + 1);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    // Allow empty string for editing
    if (newValue === '') {
      setInputValue('');
      return;
    }
    // Only allow numbers
    if (/^\d+$/.test(newValue)) {
      setInputValue(newValue);
      const num = parseInt(newValue, 10);
      if (!isNaN(num) && num >= min && num <= max) {
        onChange(num);
      }
    }
  };

  const handleBlur = () => {
    // On blur, ensure we have a valid value
    if (inputValue === '' || isNaN(parseInt(inputValue, 10))) {
      setInputValue(String(min));
      onChange(min);
    } else {
      const num = parseInt(inputValue, 10);
      const clampedValue = Math.max(min, Math.min(max, num));
      setInputValue(String(clampedValue));
      if (clampedValue !== value) {
        onChange(clampedValue);
      }
    }
  };

  // Size-based styling per spec
  const buttonSize = size === 'sm' ? 'w-6 h-6 sm:w-7 sm:h-7' : 'w-7 h-7 sm:w-8 sm:h-8';
  const iconSize = size === 'sm' ? 'w-3 h-3' : 'w-3.5 h-3.5 sm:w-4 sm:h-4';
  const inputWidth = size === 'sm' ? 'w-8 sm:w-10' : 'w-9 sm:w-12';
  const inputHeight = size === 'sm' ? 'h-6 sm:h-7' : 'h-7 sm:h-8';

  return (
    <div className="flex flex-col items-center">
      {label && (
        <label className="text-[10px] sm:text-xs text-[var(--g600)] mb-1 font-medium text-center whitespace-nowrap">
          {label}
        </label>
      )}
      <div className={`flex items-center gap-0.5 sm:gap-1 ${disabled ? 'opacity-50' : ''}`}>
        <button
          type="button"
          className={`${buttonSize} rounded-full border border-[var(--g300)] bg-white text-[var(--g500)] flex items-center justify-center transition-colors ${
            disabled || value <= min 
              ? 'cursor-not-allowed opacity-40' 
              : 'hover:border-[var(--navy)] hover:text-[var(--navy)]'
          }`}
          onClick={handleDecrement}
          disabled={disabled || value <= min}
        >
          <Minus className={iconSize} />
        </button>
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleBlur}
          disabled={disabled}
          className={`${inputWidth} ${inputHeight} text-center font-bold tabular-nums text-sm sm:text-base text-[var(--g900)] border border-[var(--g300)] rounded-md focus:outline-none focus:border-[var(--cyan)] focus:ring-2 focus:ring-[var(--cyan-ring)] ${
            disabled ? 'bg-[var(--g100)] cursor-not-allowed' : 'bg-white'
          }`}
        />
        <button
          type="button"
          className={`${buttonSize} rounded-full border border-[var(--g300)] bg-white text-[var(--g500)] flex items-center justify-center transition-colors ${
            disabled || value >= max 
              ? 'cursor-not-allowed opacity-40' 
              : 'hover:border-[var(--navy)] hover:text-[var(--navy)]'
          }`}
          onClick={handleIncrement}
          disabled={disabled || value >= max}
        >
          <Plus className={iconSize} />
        </button>
      </div>
    </div>
  );
};

export { NumberStepper };
export default NumberStepper;
